import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ActivationReport,
  DormantReport,
  ReachReport,
  ReportDimension,
  ReportFreshness,
  ReportSubscriptionList,
  BestMoment,
  ReportFrequency,
} from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { useToast } from '@up2d8/ui';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  Chip,
  Empty,
  Grid,
  Input,
  Note,
  PageHead,
  Percent,
  Segmented,
  Select,
  Stats,
  Table,
  useAdminToast,
} from '../admin-kit';
import { CardLoading, LoadError } from '../opvolging/parts';
import { saveBlob } from '../../shared/download';
import { useSession } from '../../shared/session';
import { useTenantTheme } from '../../shared/use-tenant-theme';
import { formatDateTime } from '../../shared/format';
import { ReachChart, weeksOf } from './reach-chart';
import { breakdownColumnKey } from './breakdown-column';
import './reports-engagement.css';

/** Keuzes voor de periode. Dagen en geen datumkiezer: niemand rapporteert over 17 dagen. */
const RANGES = ['30', '90', '365'] as const;

function rangeFor(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

function percentage(part: number | null, whole: number | null): number | null {
  if (part === null || whole === null || whole === 0) return null;
  return Math.round((part / whole) * 100);
}

/**
 * Het rapportagedashboard (Stap 33), in het ontwerp van BHR-7 (V1).
 *
 * Dit is het enige scherm in het platform dat voor een **directie** gemaakt is, en dat verandert de
 * vormgeving: geen lijst om door te werken, maar drie getallen die je onthoudt en één lijn die een
 * richting laat zien. Wie meer wil, exporteert.
 *
 * Twee dingen staan er bewust zichtbaar bij en zijn geen vormgeving maar eerlijkheid:
 *
 * 1. **Hoe oud de cijfers zijn**, in de zin onder de titel. Dit is een rapport dat 's nachts
 *    herrekend wordt, geen teller die meeloopt. Een dashboard dat doet alsof het live is, laat
 *    iemand morgen een beslissing nemen op cijfers van vorige week zonder dat hij dat weet.
 * 2. **Hoeveel regels er verborgen zijn.** Een rapport dat stil drie afdelingen weglaat, is een
 *    rapport waarvan niemand weet dat het incompleet is.
 */
export function ReportsPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const errorToast = useToast();
  const toast = useAdminToast();
  const queryClient = useQueryClient();
  const [days, setDays] = useState<(typeof RANGES)[number]>('30');
  const [dimension, setDimension] = useState<ReportDimension>('group');
  const range = rangeFor(Number(days));
  const periodId = useId();
  const dimensionId = useId();

  const reach = useQuery({
    queryKey: ['report-reach', range.from, range.to, dimension],
    queryFn: async (): Promise<ReachReport> => {
      const { data, error } = await api.GET('/api/v1/reports/reach', {
        params: { query: { from: range.from, to: range.to, dimension } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as ReachReport;
    },
  });

  const activation = useQuery({
    queryKey: ['report-activation', range.from, range.to],
    queryFn: async (): Promise<ActivationReport> => {
      const { data, error } = await api.GET('/api/v1/reports/activation', {
        params: { query: { from: range.from, to: range.to } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as ActivationReport;
    },
  });

  const freshness = useQuery({
    queryKey: ['report-freshness'],
    queryFn: async (): Promise<ReportFreshness | null> => {
      const { data, error } = await api.GET('/api/v1/reports/freshness', {});
      if (error || !data) return null;
      return data as ReportFreshness;
    },
  });

  /**
   * De cijfers nu bijwerken.
   *
   * Dezelfde berekening als de nachtelijke job. Een bedrijf dat net begint heeft anders tot de
   * volgende nacht een leeg dashboard, en dat leest als "we bereiken niemand" in plaats van "er is
   * nog niet gerekend".
   */
  const recompute = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/reports/recompute', {});
      if (error) throw error;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['report-reach'] }),
        queryClient.invalidateQueries({ queryKey: ['report-activation'] }),
        queryClient.invalidateQueries({ queryKey: ['report-dormant'] }),
        queryClient.invalidateQueries({ queryKey: ['report-freshness'] }),
      ]);
      toast.show(t('reports.recomputed'));
    },
    onError: (error) =>
      errorToast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  // Het maandrapport van de vorige maand: dat is het laatste dat af is.
  const now = new Date();
  const previous = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const month = previous.toISOString().slice(0, 7);

  /**
   * De pdf ophalen met het token, en daarna opslaan.
   *
   * Geen `<a href download>`: dat is een browsernavigatie zonder `Authorization`-header en geeft
   * altijd een `401`. Zie `shared/download.ts`.
   */
  const downloadPdf = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.GET('/api/v1/reports/monthly/{month}.pdf', {
        params: { path: { month } },
        parseAs: 'blob',
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      await saveBlob(data as unknown as Blob, `maandrapport-${month}.pdf`);
    },
    onError: () => errorToast.show({ tone: 'danger', title: t('reports.downloadFailed') }),
  });

  const totals = (reach.data?.series ?? []).reduce(
    (sum, point) => ({
      recipients: sum.recipients + point.recipients,
      seen: sum.seen + point.seen,
      acknowledged: sum.acknowledged + point.acknowledged,
      posts: sum.posts + point.posts,
      interactions: sum.interactions + (point.interactions ?? 0),
      clicks: sum.clicks + (point.clicks ?? 0),
    }),
    { recipients: 0, seen: 0, acknowledged: 0, posts: 0, interactions: 0, clicks: 0 },
  );

  return (
    <AdminPage>
      <PageHead
        title={t('reports.title')}
        sub={
          <>
            {t('beheer.opvolging.reports.sub')}{' '}
            {/* Hoe oud de cijfers zijn. Een dashboard dat doet alsof het live is, laat iemand een
                beslissing nemen op cijfers van vorige week zonder dat hij dat weet. */}
            <span data-testid="report-freshness">
              {freshness.data?.computedAt
                ? t('beheer.opvolging.reports.computedAt', {
                    when: formatDateTime(freshness.data.computedAt),
                  })
                : t('reports.notComputedYet')}
            </span>
          </>
        }
        actions={
          <>
            <Btn
              variant="sec"
              icon={<AdminIcon name="rotate" />}
              testId="report-recompute"
              disabled={recompute.isPending}
              aria-busy={recompute.isPending}
              onClick={() => recompute.mutate()}
            >
              {t('beheer.opvolging.reports.recompute')}
            </Btn>
            <Btn
              variant="sec"
              icon={<AdminIcon name="download" />}
              testId="monthly-pdf"
              title={t('beheer.opvolging.reports.pdfTitle', { month })}
              disabled={downloadPdf.isPending}
              aria-busy={downloadPdf.isPending}
              onClick={() => downloadPdf.mutate()}
            >
              {t('beheer.opvolging.reports.pdf')}
            </Btn>
          </>
        }
      />

      <div className="a-toolbar a-card">
        <span className="a-label" id={periodId}>
          {t('reports.period')}
        </span>
        <Segmented
          labelledBy={periodId}
          value={days}
          onChange={setDays}
          options={RANGES.map((value) => ({
            value,
            label: t('beheer.opvolging.reports.days', { count: Number(value) }),
            testId: `report-range-${value}`,
          }))}
        />
        <label className="a-label" htmlFor={dimensionId} style={{ marginLeft: 12 }}>
          {t('reports.dimension')}
        </label>
        <div style={{ width: 260 }}>
          <Select
            id={dimensionId}
            value={dimension}
            data-testid="report-dimension"
            onChange={(event) => setDimension(event.target.value as ReportDimension)}
          >
            <option value="group">{t('reports.byGroup')}</option>
            <option value="kind">{t('beheer.opvolging.reports.byKind')}</option>
            <option value="type">{t('reportsEngagement.byType')}</option>
            <option value="tenant">{t('beheer.opvolging.reports.wholeCompany')}</option>
          </Select>
        </div>
      </div>

      {reach.isPending ? (
        <Card>
          <CardLoading rows={3} />
        </Card>
      ) : reach.isError ? (
        <Card>
          <LoadError onRetry={() => void reach.refetch()} error={reach.error} />
        </Card>
      ) : (
        <Card label={t('beheer.opvolging.reports.statsLabel')}>
          <CardPad>
            <Stats
              items={[
                {
                  label: t('reports.postsSent'),
                  value: totals.posts,
                  sub: t('beheer.opvolging.reports.postsSub', { count: totals.recipients }),
                  testId: 'metric-posts',
                },
                {
                  label: t('reports.seenRate'),
                  value: `${percentage(totals.seen, totals.recipients) ?? 0}%`,
                  sub: t('beheer.opvolging.reports.ofTotal', {
                    part: totals.seen,
                    whole: totals.recipients,
                  }),
                  testId: 'metric-seen',
                },
                {
                  label: t('reports.acknowledgedRate'),
                  value: `${percentage(totals.acknowledged, totals.recipients) ?? 0}%`,
                  sub: t('beheer.opvolging.reports.ofTotal', {
                    part: totals.acknowledged,
                    whole: totals.recipients,
                  }),
                  testId: 'metric-acknowledged',
                },
                // FBK-11: interacties gedeeld door ontvangers, en klikken op links.
                {
                  label: t('reportsEngagement.engagement'),
                  value: `${percentage(totals.interactions, totals.recipients) ?? 0}%`,
                  sub: t('reportsEngagement.engagementSub', { count: totals.interactions }),
                  testId: 'metric-engagement',
                },
                {
                  label: t('reportsEngagement.clicks'),
                  value: totals.clicks,
                  sub: t('reportsEngagement.clicksSub'),
                  testId: 'metric-clicks',
                },
              ]}
            />
          </CardPad>
        </Card>
      )}

      <Grid variant="side">
        <Card>
          <CardHead
            title={t('beheer.opvolging.reports.chartTitle')}
            actions={<span className="a-meta">{t('beheer.opvolging.reports.chartUnit')}</span>}
          />
          <CardPad>
            {reach.isPending ? (
              <CardLoading rows={4} bare />
            ) : reach.data ? (
              <>
                <ReachChart weeks={weeksOf(reach.data.series)} />
                <div className="a-actions a-small">
                  <span className="a-chip" style={{ background: 'var(--brand-primary-tint)' }}>
                    {t('beheer.opvolging.reports.legendSent')}
                  </span>
                  <span
                    className="a-chip"
                    style={{
                      background: 'var(--brand-dark-blue-tint)',
                      color: 'var(--brand-dark-blue)',
                    }}
                  >
                    {t('reports.legendSeen')}
                  </span>
                </div>
              </>
            ) : null}
          </CardPad>
        </Card>
        <ActivationCard report={activation.data} pending={activation.isPending} />
      </Grid>

      {reach.data ? <Breakdown report={reach.data} range={range} dimension={dimension} /> : null}
      <BestMomentCard range={range} />
      <DormantNamesCard />
      <MonthlyCard onSaved={toast.show} />
      {toast.element}
    </AdminPage>
  );
}

function Breakdown({
  report,
  range,
  dimension,
}: {
  report: ReachReport;
  range: { from: string; to: string };
  dimension: ReportDimension;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();

  /** Dezelfde reden als bij de pdf: een platte link draagt het token niet. */
  const download = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.GET('/api/v1/reports/reach.csv', {
        params: { query: { from: range.from, to: range.to, dimension } },
        parseAs: 'blob',
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      await saveBlob(data as unknown as Blob, `bereik-${range.from}-tot-${range.to}.csv`);
    },
    onError: () => toast.show({ tone: 'danger', title: t('reports.downloadFailed') }),
  });

  return (
    <Card>
      <CardHead
        title={t('reports.breakdown')}
        actions={
          <Btn
            variant="sec"
            size="sm"
            icon={<AdminIcon name="download" />}
            testId="report-csv"
            disabled={download.isPending}
            aria-busy={download.isPending}
            onClick={() => download.mutate()}
          >
            {t('reports.exportCsv')}
          </Btn>
        }
      />

      {report.suppressedLines > 0 ? (
        // Een rapport dat stil regels weglaat, is een rapport waarvan niemand weet dat het
        // incompleet is.
        <CardPad>
          <Note
            tone="neutral"
            icon={<AdminIcon name="eyeOff" size={18} />}
            testId="suppressed-notice"
          >
            {t('reports.suppressedNotice', { count: report.suppressedLines })}
          </Note>
        </CardPad>
      ) : null}

      {report.breakdown.length === 0 ? (
        <Empty
          icon={<AdminIcon name="eyeOff" size={26} />}
          title={t('reports.emptyTitle')}
          text={t('reports.emptyHint')}
        />
      ) : (
        <Table label={t('reports.breakdown')}>
          <thead>
            <tr>
              <th>{t(breakdownColumnKey(dimension))}</th>
              <th className="num">{t('reports.colRecipients')}</th>
              <th>{t('reports.colSeen')}</th>
              <th>{t('reports.colAcknowledged')}</th>
              {dimension === 'type' ? (
                <>
                  <th className="num">{t('reportsEngagement.colPosts')}</th>
                  <th>{t('reportsEngagement.engagement')}</th>
                  <th className="num">{t('reportsEngagement.clicks')}</th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {report.breakdown.map((line) => (
              <tr key={line.dimensionId || 'tenant'} data-testid="breakdown-row">
                <td>{line.label}</td>
                {line.suppressed ? (
                  <td colSpan={dimension === 'type' ? 6 : 3}>
                    <span className="a-muted a-small">{t('reports.tooSmall')}</span>
                  </td>
                ) : (
                  <>
                    <td className="num">{line.recipients ?? 0}</td>
                    <td>
                      <PercentOrDash value={percentage(line.seen, line.recipients)} />
                    </td>
                    <td>
                      <PercentOrDash value={percentage(line.acknowledged, line.recipients)} />
                    </td>
                    {dimension === 'type' ? (
                      <>
                        <td className="num">{line.posts ?? 0}</td>
                        <td>
                          <PercentOrDash value={percentage(line.interactions, line.recipients)} />
                        </td>
                        <td className="num">{line.clicks ?? 0}</td>
                      </>
                    ) : null}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

/** Een balk met percentage, of een streep als er niemand ontving: 0 van 0 is geen 0%. */
function PercentOrDash({ value }: { value: number | null }) {
  return value === null ? <span className="a-muted">—</span> : <Percent value={value} />;
}

/**
 * De activatietrechter (V1, rechts): van in dienst naar werkelijk in gebruik, met tussen de stappen
 * de uitval in oranje. Daaronder het aantal slapende medewerkers.
 */
function ActivationCard({
  report,
  pending,
}: {
  report: ActivationReport | undefined;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const current = report?.current;

  let funnel: ReactNode = null;
  if (current) {
    const steps = [
      { label: t('reports.funnelPeople'), value: current.people },
      { label: t('reports.funnelAccounts'), value: current.accounts },
      { label: t('reports.funnelActivated'), value: current.activated },
    ];
    // Tussen de stappen: wie er afvalt. "Nog niet aangemeld" is het getal van de server en niet
    // het verschil, want een uitgeschakeld account is niet "nog niet aangemeld".
    const drops = [
      t('beheer.opvolging.reports.dropNoAccount', {
        count: Math.max(0, current.people - current.accounts),
      }),
      t('beheer.opvolging.reports.dropPending', { count: current.pending }),
    ];
    funnel = (
      <div className="a-funnel" data-testid="activation-funnel">
        {steps.map((step, index) => {
          const share = percentage(step.value, current.people) ?? 0;
          return (
            <div key={step.label} style={{ display: 'contents' }}>
              {index > 0 ? <div className="a-funnel-drop">{drops[index - 1]}</div> : null}
              <div className="a-funnel-row">
                <span className="a-name">{step.label}</span>
                <div className="a-funnel-bar" aria-hidden="true">
                  <span style={{ width: `${Math.min(100, share)}%` }} />
                </div>
                <span className="a-pct">
                  <b>{step.value}</b>&nbsp;· {share}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <Card>
      <CardHead title={t('reports.activation')} />
      <CardPad>
        {pending ? (
          <CardLoading rows={3} bare />
        ) : !current ? (
          <Empty
            icon={<AdminIcon name="rotate" size={26} />}
            title={t('reports.activationEmptyTitle')}
            text={t('reports.activationEmptyHint')}
          />
        ) : (
          funnel
        )}
        <DormantNote />
      </CardPad>
    </Card>
  );
}

function useDormant() {
  const { api } = useSession();
  return useQuery({
    queryKey: ['report-dormant'],
    queryFn: async (): Promise<DormantReport | null> => {
      const { data, error } = await api.GET('/api/v1/reports/dormant', {});
      if (error || !data) return null;
      return data as DormantReport;
    },
  });
}

/**
 * De slapende medewerkers, als oranje vlak onder de trechter.
 *
 * Het **aantal** staat er altijd; de namen alleen met het tweede recht én de tenantvlag. Als die
 * ontbreken, zegt het vlak wélke van de twee: "je mist een recht" is een ander gesprek dan "je
 * bedrijf heeft dit uitgezet".
 */
function DormantNote() {
  const { t } = useTranslation();
  const theme = useTenantTheme();
  const report = useDormant().data;
  if (!report) return null;

  return (
    <Note
      tone={report.total > 0 ? 'attention' : 'neutral'}
      icon={<AdminIcon name="moon" size={18} />}
      testId="dormant-total"
    >
      <strong>{t('beheer.opvolging.reports.dormantCount', { count: report.total })}</strong>{' '}
      {t('beheer.opvolging.reports.dormantText', {
        count: report.total,
        days: report.thresholdDays,
      })}
      {!report.namesVisible ? (
        <>
          {' '}
          <span data-testid="dormant-withheld">
            {t(
              `beheer.opvolging.reports.dormantWithheld.${report.namesWithheldReason ?? 'no_permission'}`,
              { company: theme?.displayName || t('beheer.opvolging.reports.yourCompany') },
            )}
          </span>
        </>
      ) : null}
    </Note>
  );
}

/**
 * De namen van de slapende medewerkers, als het mag. Niet in de mockup (die toont alleen het
 * aantal), maar het bestond al en het is waar een HR-medewerker mee verder kan.
 */
function DormantNamesCard() {
  const { t } = useTranslation();
  const report = useDormant().data;
  if (!report || !report.namesVisible || report.total === 0) return null;

  return (
    <Card testId="dormant-names">
      <CardHead title={t('reports.dormant')} count={report.items.length} />
      {report.items.length === 0 ? (
        <Empty
          icon={<AdminIcon name="moon" size={26} />}
          title={t('reports.dormantEmptyTitle')}
          text={t('reports.dormantEmptyHint')}
        />
      ) : (
        <Table label={t('reports.dormant')}>
          <thead>
            <tr>
              <th>{t('reports.colName')}</th>
              <th>{t('reports.colLastSeen')}</th>
              <th>{t('reports.colPush')}</th>
              <th>{t('reports.colAccount')}</th>
            </tr>
          </thead>
          <tbody>
            {report.items.map((person) => (
              <tr key={person.personId} data-testid="dormant-row">
                <td>
                  <span className="a-name">{person.displayName}</span>
                  {person.employeeNumber ? (
                    <span className="a-meta"> · {person.employeeNumber}</span>
                  ) : null}
                </td>
                <td>
                  {person.daysSinceLastSeen === null ? (
                    // Nooit aangemeld is geen "heel veel dagen" maar een andere toestand: daar is
                    // de activatiebrief niet aangekomen.
                    <Badge tone="attention">{t('reports.neverSeen')}</Badge>
                  ) : (
                    t('reports.daysAgo', { count: person.daysSinceLastSeen })
                  )}
                </td>
                <td>
                  {person.pushEnabled ? (
                    <span className="a-muted">{t('reports.pushOn')}</span>
                  ) : (
                    <Badge tone="danger">{t('reports.pushOff')}</Badge>
                  )}
                </td>
                <td className="a-muted">{t(`reports.accountStatus.${person.accountStatus}`)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

/** "Maandrapport per mail": wie het rapport de eerste van de maand krijgt. */
function MonthlyCard({ onSaved }: { onSaved: (message: string) => void }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [frequency, setFrequency] = useState<ReportFrequency>('monthly');

  const subscriptions = useQuery({
    queryKey: ['report-subscriptions'],
    queryFn: async (): Promise<ReportSubscriptionList> => {
      const { data, error } = await api.GET('/api/v1/reports/subscriptions', {});
      if (error || !data) return { items: [] };
      return data as ReportSubscriptionList;
    },
  });

  const add = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/reports/subscriptions', {
        body: { email: email.trim(), frequency },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setEmail('');
      void queryClient.invalidateQueries({ queryKey: ['report-subscriptions'] });
      onSaved(t('reports.subscriptionAdded'));
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  const remove = useMutation({
    mutationFn: async (subscriptionId: string) => {
      const { error } = await api.DELETE('/api/v1/reports/subscriptions/{subscriptionId}', {
        params: { path: { subscriptionId } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['report-subscriptions'] });
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  const changeFrequency = useMutation({
    mutationFn: async (input: { subscriptionId: string; frequency: ReportFrequency }) => {
      const { error } = await api.PATCH('/api/v1/reports/subscriptions/{subscriptionId}', {
        params: { path: { subscriptionId: input.subscriptionId } },
        body: { frequency: input.frequency } as never,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['report-subscriptions'] });
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  const active = (subscriptions.data?.items ?? []).filter((row) => row.enabled);

  return (
    <Card>
      <CardHead title={t('beheer.opvolging.reports.monthlyTitle')} />
      <CardPad>
        <div className="a-hint">{t('beheer.opvolging.reports.monthlyHint')}</div>
        {active.length === 0 ? (
          <div className="a-muted a-small">{t('reports.noRecipients')}</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {active.map((subscription) => (
              <div key={subscription.id} className="a-inline" style={{ maxWidth: 460 }}>
                <Chip testId="subscription-row" onRemove={() => remove.mutate(subscription.id)}>
                  {subscription.email}
                </Chip>
                {/* Hoe vaak deze ontvanger het rapport krijgt (FBK-11). */}
                <div style={{ width: 170 }}>
                  <Select
                    aria-label={t('reportsEngagement.frequency')}
                    value={subscription.frequency}
                    data-testid="subscription-frequency"
                    onChange={(event) =>
                      changeFrequency.mutate({
                        subscriptionId: subscription.id,
                        frequency: event.target.value as ReportFrequency,
                      })
                    }
                  >
                    {FREQUENCIES.map((option) => (
                      <option key={option} value={option}>
                        {t(`reportsEngagement.freq.${option}`)}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            ))}
          </div>
        )}
        <form
          className="a-inline"
          style={{ maxWidth: 460 }}
          onSubmit={(event) => {
            event.preventDefault();
            if (email.trim()) add.mutate();
          }}
        >
          <Input
            type="email"
            aria-label={t('reports.addRecipient')}
            placeholder={t('beheer.opvolging.reports.emailPlaceholder')}
            value={email}
            data-testid="subscription-email"
            onChange={(event) => setEmail(event.target.value)}
          />
          <div style={{ width: 170 }}>
            <Select
              aria-label={t('reportsEngagement.frequency')}
              value={frequency}
              data-testid="subscription-new-frequency"
              onChange={(event) => setFrequency(event.target.value as ReportFrequency)}
            >
              {FREQUENCIES.map((option) => (
                <option key={option} value={option}>
                  {t(`reportsEngagement.freq.${option}`)}
                </option>
              ))}
            </Select>
          </div>
          <Btn
            type="submit"
            variant="sec"
            size="sm"
            testId="subscription-add"
            disabled={email.trim().length === 0 || add.isPending}
            aria-busy={add.isPending}
          >
            {t('reports.add')}
          </Btn>
        </form>
      </CardPad>
    </Card>
  );
}

const FREQUENCIES: ReportFrequency[] = ['weekly', 'biweekly', 'monthly'];

/**
 * Wanneer er gelezen wordt (FBK-11): "gezien" per weekdag en uur, als warmtekaart. Het donkerste vak
 * is het moment waarop de meeste mensen een bericht openen; daar hoort een belangrijk bericht kort
 * voor te vertrekken.
 */
function BestMomentCard({ range }: { range: { from: string; to: string } }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const moment = useQuery({
    queryKey: ['report-best-moment', range.from, range.to],
    queryFn: async (): Promise<BestMoment> => {
      const { data, error } = await api.GET('/api/v1/reports/best-moment', {
        params: { query: { from: range.from, to: range.to } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as BestMoment;
    },
  });

  const grid = moment.data?.grid ?? [];
  const max = Math.max(1, ...grid.flat());
  let best: { day: number; hour: number; count: number } | null = null;
  grid.forEach((row, day) =>
    row.forEach((count, hour) => {
      if (count > 0 && (!best || count > best.count)) best = { day, hour, count };
    }),
  );
  const dayNames = [1, 2, 3, 4, 5, 6, 7].map((day) =>
    new Date(Date.UTC(2026, 9, 25 + day, 12)).toLocaleDateString(undefined, { weekday: 'short' }),
  );
  const top = best as { day: number; hour: number; count: number } | null;

  return (
    <Card testId="best-moment">
      <CardHead
        title={t('reportsEngagement.bestMomentTitle')}
        actions={moment.data ? <span className="a-meta">{moment.data.timeZone}</span> : undefined}
      />
      <CardPad>
        {moment.isPending ? (
          <CardLoading rows={3} bare />
        ) : moment.isError ? (
          <LoadError onRetry={() => void moment.refetch()} error={moment.error} />
        ) : moment.data?.suppressed || grid.length === 0 || !top ? (
          <div className="a-muted a-small">{t('reportsEngagement.bestMomentEmpty')}</div>
        ) : (
          <>
            <p style={{ marginTop: 0 }} data-testid="best-moment-top">
              {t('reportsEngagement.bestMomentTop', {
                day: dayNames[top.day],
                from: `${String(top.hour).padStart(2, '0')}:00`,
                to: `${String((top.hour + 1) % 24).padStart(2, '0')}:00`,
              })}
            </p>
            <div
              className="c-heatmap"
              role="img"
              aria-label={t('reportsEngagement.bestMomentTitle')}
            >
              <div />
              {Array.from({ length: 24 }, (_, hour) => (
                <div key={hour} className="c-heatmap-hour">
                  {hour % 3 === 0 ? hour : ''}
                </div>
              ))}
              {grid.map((row, day) => (
                <Fragment key={day}>
                  <div className="c-heatmap-day">{dayNames[day]}</div>
                  {row.map((count, hour) => (
                    <div
                      key={hour}
                      className="c-heatmap-cell"
                      title={`${dayNames[day]} ${hour}:00 — ${count}`}
                      style={{
                        background:
                          count === 0
                            ? 'var(--background)'
                            : `color-mix(in srgb, var(--brand-primary) ${Math.round(15 + (count / max) * 85)}%, white)`,
                      }}
                    />
                  ))}
                </Fragment>
              ))}
            </div>
          </>
        )}
      </CardPad>
    </Card>
  );
}
